import { useTranslation } from 'react-i18next';

// Utils
import cn from '../../../../utils/cn';

// Styles
import styles from './DirectEditActionsRender.module.css';
import directEditCommon from '../DirectEditCommon.module.css';

// Store
import { Popover } from '../../../../store/popover/PopoverContext';

// Components
import { PopoverTrigger, PopoverContent } from '../../../atoms/Popover/Popover';
import ColumnsVisibility from '../../ColumnsVisibility/ColumnsVisibility';
import SearchGroupList from '../../SearchGroupList/SearchGroupList';
import IconButton from '../../../atoms/IconButton/IconButton';

// Domain
import useTableDirectEditActions from '../../../../domain/tableDirectEdit/useTableDirectEditActions';

// Table cell layout
import { getDirectEditShellProps } from '../getDirectEditShellClassName';
import DirectEditLoadingCell from '../DirectEditLoadingCell/DirectEditLoadingCell';
import DirectEditValueSlot from '../DirectEditValueSlot/DirectEditValueSlot';

/**
 * DirectEditActionsRender component that displays a popover with actions for actionChoice direct edit
 * @param {object} props
 * @param {TableCellValueObjectCustom} props.cellValue - Cell value with directEdit config
 * @param {ApiManagerTableColumns} props.column - Column configuration
 * @param {React.ReactNode} props.children - The cell content to display as trigger
 * @param {(editing: boolean) => void} [props.onEditingChange] - Optional; used by CellHoverPreviewWrapper to suppress hover
 * @param {boolean} [props.initialOpen=false] - Open the actions popover on mount
 * @returns {React.ReactNode}
 */
function DirectEditActionsRender({
  cellValue,
  column,
  children,
  onEditingChange,
  initialOpen = false,
}) {
  const { t } = useTranslation();
  const {
    handleActionByType,
    actionsIsOpen,
    setActionsIsOpen,
    hasActions,
    actions,
    isMutationLoading,
  } = useTableDirectEditActions({
    cellValue, column, onEditingChange, initialOpen,
  });

  if (isMutationLoading) {
    return (
      <DirectEditLoadingCell cellValue={cellValue}>
        {children}
      </DirectEditLoadingCell>
    );
  }

  const { className: shellClassName, style: shellStyle } = getDirectEditShellProps(cellValue);

  if (!hasActions) {
    return (
      <div className={shellClassName} style={shellStyle}>
        {children}
      </div>
    );
  }

  return (
    <div className={shellClassName} style={shellStyle}>
      <Popover
        open={actionsIsOpen}
        onOpenChange={setActionsIsOpen}
        placement="bottom-start"
        offsetX={-5}
        offsetY={0}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn([
              directEditCommon['direct-edit-value-control'],
              directEditCommon['direct-edit-value-control-action'],
            ])}
            onClick={(e) => {
              e.stopPropagation();
              setActionsIsOpen(true);
            }}
          >
            <DirectEditValueSlot action={null}>{children}</DirectEditValueSlot>
          </button>
        </PopoverTrigger>
        <PopoverContent>
          <div className={styles['direct-edit-actions-container']}>
            <ColumnsVisibility options={actions} messageNoOptions={t('table.filters.noResult')}>
              {(label, options) => (
                <SearchGroupList
                  key={label}
                  hasLabel={!!label}
                  items={options}
                  label={label}
                >
                  {(option) => (
                    <button
                      key={option.id}
                      type="button"
                      className={styles['direct-edit-actions-item']}
                      onClick={(e) => {
                        // Popover is portaled but React events still bubble to the row cell
                        e.stopPropagation();
                        handleActionByType(option, option.type);
                      }}
                    >
                      <span className={styles['direct-edit-actions-label']}>
                        {option.lib}
                      </span>
                      {option.icon && (
                        <IconButton
                          readOnly
                          icon={option.icon}
                          color="transparent"
                          size="small"
                        />
                      )}
                    </button>
                  )}
                </SearchGroupList>
              )}
            </ColumnsVisibility>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default DirectEditActionsRender;
